/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Illya Babanov
 * @version 1.23.0
 * @since 1.19.0
 */

import { useSelector } from 'react-redux';

import { checkESTableList } from '@utils/checkESTable';

import {
    tableContext,
    tableDataCountSelector,
    tableDataHasMoreSelector,
    tableDataLoadSizeSelector,
    tableDataSelector,
    tableListIsTemplateSelector,
    tableListUrlSelector,
    tableSearchAfterValuesSelector,
} from '../selectors';
import { useLazyGetTableListQuery } from '../store';

export const useTableListApi = () => {
    /* API */
    const [trigger] = useLazyGetTableListQuery();

    /* Selectors */
    const listUrl = useSelector(tableListUrlSelector);
    const isTemplate = useSelector(tableListIsTemplateSelector);

    const tableData = useSelector(tableDataSelector);
    const tableDataHasMore = useSelector(tableDataHasMoreSelector);
    const tableDataCount = useSelector(tableDataCountSelector);
    const tableLoadSize = useSelector(tableDataLoadSizeSelector);
    const tableSearchAfterValues = useSelector(tableSearchAfterValuesSelector);

    const context = useSelector(tableContext);

    const loadMore = () => {
        if (!listUrl) {
            // eslint-disable-next-line no-throw-literal
            throw '[Table]: incorrect table configuration. Set listUrl in initialize method';
        }

        if (!tableData.length || !tableDataHasMore) return;

        let search_after_values: string[] | null | undefined = undefined;

        if (!!tableSearchAfterValues && Array.isArray(tableSearchAfterValues) && !!tableSearchAfterValues.length) {
            search_after_values = tableSearchAfterValues;
        }

        trigger({
            url: listUrl,
            from: tableData.length,
            size: tableLoadSize,
            isTemplate,
            use_search_after: checkESTableList(listUrl),
            search_after_values,
            context,
        });
    };

    const initialLoad = () => {
        if (!listUrl) {
            // eslint-disable-next-line no-throw-literal
            throw '[Table]: incorrect table configuration. Set listUrl in initialize method';
        }

        trigger(
            {
                url: listUrl,
                size: tableLoadSize,
                from: 0,
                use_search_after: checkESTableList(listUrl),
                context,
            },
            false,
        );
    };

    return {
        count: tableDataCount || 0,
        data: tableData,
        isCompact: tableLoadSize === 32,

        initialLoad,

        /* Inifinite loader */
        infiniteLoader: {
            hasMore: tableDataHasMore,
            initialLoad: false,
            pageStart: 0,
            loadMore,
        },
    };
};
